import clsx from 'clsx'
import useNewDesign from '../../../../hooks/useNewDesign'
import ProcessListV2 from './ProcessListV2'
import TemplateListV2 from './TemplateListV2'
import FolderPath from './FolderPath'
import {
  Box, Button as MuiButton, Hidden, useMediaQuery, useTheme
} from '@mui/material'
import { AxiosError } from 'axios'
import {
  useCallback, useEffect, useMemo, useRef, useState
} from 'react'
import { confirmAlert } from 'react-confirm-alert'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import { styled } from '@mui/material/styles'
import { setActiveResultsSort, setCreatedTempaltesSort } from '../../../../actions/sortActions'
import api from '../../../../api'
import DateUtil from '../../../../util/DateUtil'
import { IFolder, ListSummary, QuickFilter } from '../../../../api/FolderApi'
import { useIsAdmin, useIsPowerUser } from '../../../../hooks/authHooks'
import useFolderData from '../../../../hooks/folder/useFolderData'
import useFolderTableHeaders from '../../../../hooks/folder/useFolderTableHeaders'
import useTableData from '../../../../hooks/folder/useTableData'
import useCorporationSettings from '../../../../hooks/useCorporationSettings'
import { IAlphaprocessState } from '../../../../store'
import LoadSpinner from '../../../loadSpinner/LoadSpinner'
import DataTable, { DataTableData } from '../../../widgets/DataTable'
import FolderBreadCrumbs from './FolderBreadCrumbs'
import MoveFolderDialog from './MoveFolderDialog'
import NameFolderDialog from './RenameFolderDialog'
import SplitButton from './SplitButton'
import { confirm, alert } from '../../../../util'
import EditModalWrapper from '../EditModalWrapper'
import ErrorView from '../../../ErrorView'

export enum FolderMode {
  templates,
  checklists
}

interface Props {
  folder: IFolder
  mode: FolderMode
  selectedId?: string | number
  onSelect?: (x: any) => void
  onUpdateFolder: (id: string) => void
  checklistStatus: 'open' | 'closed' | 'due' | 'invited'
  onError: (e: AxiosError) => void
  tabIndex: number
  onSummary?: (summary: ListSummary | null) => void
}

export interface TableData extends DataTableData {
  type: 'folder' | 'result_set' | 'template'
  title: string | JSX.Element
  due_date?: string | JSX.Element
  created_at?: string | JSX.Element
  tester?: string | JSX.Element
  creator?: string | JSX.Element
  can_delete: boolean
  status: 'open' | 'closed' | 'reopened' | 'planned'
  checklist_id?: number
  isOffline: boolean
  // new design list (hooks/folder/useTableData)
  hints?: string | JSX.Element
}

function getBulkIds(items: any[]) {
  const folderIds = items.filter((x) => x.type === 'folder').map(((x) => x.id))
  const checklistIds = items.filter((x) => x.type === 'result_set').map((x) => x.id)
  const templateIds = items.filter((x) => x.type === 'template').map((x) => x.id)

  return { folderIds, checklistIds, templateIds }
}

const TableStyles = styled('div')(({ theme }) => ({
  '& tbody tr': {
    [theme.breakpoints.down('lg')]: {
      height: '70px',
      '& .checkbox': {
        marginTop: 'calc(50% + 5px)'
      },
      '& .gear-menu': {
        marginTop: 'calc(50% - 5px)'
      },
      '& .folder-title': {
        marginTop: '12px'
      }
    }
  }
}))

export default function Folder({
  folder, onUpdateFolder, mode, checklistStatus, onError, tabIndex, selectedId, onSelect, onSummary
}: Props) {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const isAdmin = useIsPowerUser()
  // closed processes may only be deleted by admins (not power users)
  const isCorporationAdmin = useIsAdmin()
  const theme = useTheme()
  const isMobileOnly = useMediaQuery(theme.breakpoints.down('sm'))
  const { use_offline_mode } = useCorporationSettings()
  const [page, setPage] = useState(1)
  // new design list: quick filter (mine, overdue, today, with deviation)
  const [quick, setQuick] = useState<QuickFilter | null>(null)
  const [lastNewFolderId, setLastNewFolderId] = useState<string | null>(null)
  const isOffline = useSelector((state: IAlphaprocessState) => state.core.isOffline)
  const sort = useSelector((state: IAlphaprocessState) => state.sort)
  const filters = useSelector((state: IAlphaprocessState) => state.filter.checklistFilterSettings)
  const [selectedEditResultSet, setSelectedEditResultSet] = useState<number | null>(null)

  // ── Infinite scroll state (mobile only) ────────────────────────────────────
  const [mobileItems, setMobileItems] = useState<any[]>([])
  const sentinelRef = useRef<HTMLDivElement>(null)
  const loadingMoreRef = useRef(false)
  // Track current page in a ref so the data-accumulation effect can read it
  // without being listed as a dependency (avoids stale closure + double-append).
  const pageRef = useRef(page)
  useEffect(() => { pageRef.current = page }, [page])

  const {
    data,
    isLoading,
    error,
    totalPages,
    handleResultSetUpdated,
    offlineIds,
    handleClone,
    refresh,
    updateOfflineChecklist,
    handleDeleteTemplate,
    summary
  } = useFolderData(checklistStatus, folder, mode, page, tabIndex, quick)

  // Accumulate pages on mobile; replace when page 1 (fresh / reset load).
  useEffect(() => {
    if (!isMobileOnly) return
    if (pageRef.current === 1) {
      setMobileItems(data)
    } else {
      setMobileItems((prev) => [...prev, ...data])
    }
    loadingMoreRef.current = false
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]) // intentionally excludes `page` — read via pageRef instead

  // Reset to page 1 (and clear accumulator) whenever the base query changes.
  // This covers: filter changes, sort changes, folder navigation, tab switches.
  const sortSettings = useMemo(
    () => {
      if (mode === FolderMode.templates) return sort.createdTemplatesSort
      if (mode === FolderMode.checklists) return sort.activeResultsSort
      return { direction: 'asc', column: 'id' }
    },
    [mode, sort.activeResultsSort, sort.createdTemplatesSort]
  )

  const queryKey = useMemo(() => JSON.stringify({
    folderId: folder.id,
    checklistStatus,
    sort: sortSettings,
    term: filters.term,
    projects: filters.selectedProjects.map((x: any) => x.value),
    testers: filters.selectedTesters.map((x: any) => x.value),
    contacts: filters.selectedContacts.map((x: any) => x.value),
    teams: filters.selectedTeams.map((x: any) => x.value),
    ressources: filters.selectedRessources.map((x: any) => x.value),
  }), [folder.id, checklistStatus, sortSettings, filters])

  const prevQueryRef = useRef<string | null>(null)
  useEffect(() => {
    if (prevQueryRef.current !== null && prevQueryRef.current !== queryKey) {
      setPage(1)
      setMobileItems([])
    }
    prevQueryRef.current = queryKey
  }, [queryKey])

  // IntersectionObserver: trigger next-page load when sentinel enters viewport.
  useEffect(() => {
    if (!isMobileOnly || !sentinelRef.current) return
    const el = sentinelRef.current
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          entry.isIntersecting
          && !isLoading
          && !loadingMoreRef.current
          && page < totalPages
        ) {
          loadingMoreRef.current = true
          setPage((prev) => prev + 1)
        }
      },
      { rootMargin: '200px' } // start loading 200px before sentinel is visible
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [isMobileOnly, isLoading, page, totalPages])

  const tableData = useTableData(
    isMobileOnly ? mobileItems : data,
    offlineIds,
    mode,
    onUpdateFolder,
    lastNewFolderId,
    checklistStatus,
    tabIndex,
    folder
  )

  const headers = useFolderTableHeaders(!!selectedId, mode, checklistStatus)

  const checkValidEditItem = (item: any) => {
    if (item.type === 'folder') {
      return item.can_delete || isAdmin;
    }
    return (item.can_delete || isAdmin) && (item.type === 'result_set' && item.status === 'open');
  }

  // sortSettings is defined above inside the infinite-scroll block (shared)

  const handleEditModalClose = useCallback(() => {
    setSelectedEditResultSet(null)
  }, [])

  const handleRenameFolder = useCallback(async (id, name: string) => {
    const updatedFolder = await api.folders.updateFolder(id, { name })
    onUpdateFolder(updatedFolder.id)
  }, [onUpdateFolder])

  const handleEditStart = useCallback(({ id, type }: { id: number | string; type: 'folder' | 'result_set' }) => {
    if (type === 'result_set') {
      setSelectedEditResultSet(id as number)
    } else {
      const customUI = ({ onClose }) => (
        <NameFolderDialog
          onClose={onClose}
          onEdit={(name) => handleRenameFolder(id, name)}
          folder={folder.children.find((x) => x.id === id)}
          mode="rename"
        />
      )

      confirmAlert({ customUI })
    }
  }, [folder.children, handleRenameFolder])

  const handleReopen = useCallback(async ({ id }) => {
    confirm(null, t('resultSet.confirmReopen'), async () => {
      try {
        const reopened = await api.resultSets.reopen(0, id)
        const { folder_id, checklist_id } = reopened.data
        history.push(`/checklists/tab/0/folders/${folder_id}/${checklist_id}/audit/${id}`)
      } catch (e) {
        onError(e as AxiosError)
      }
    })
  }, [history, onError, t])

  // overdue and locked by the template: admins and power users open it for 24 hours
  const handleUnlock = useCallback(async ({ id, checklist_id }) => {
    confirm(null, t('resultSet.confirmUnlock'), async () => {
      try {
        const { data } = await api.resultSets.unlock(checklist_id, id)
        toast(t('resultSet.unlocked', { until: DateUtil.format(data.editable_until, 'dd.MM.yyyy, HH:mm') }), { type: 'success' })
        refresh()
      } catch (e) {
        onError(e as AxiosError)
      }
    })
  }, [onError, refresh, t])

  const handleMove = useCallback(async (item, target) => {
    if (item.type === 'folder') {
      if (target.id === item.id) {
        toast(t('folder.cannotMoveToSelf'), { type: 'error' })
        return
      }
      await api.folders.updateFolder(item.id, { parent_id: target.id })
      const updatedFolder = {
        ...folder,
        children: folder.children.filter((f) => f.id !== item.id)
      }
      onUpdateFolder(updatedFolder.id)
    } else if (item.type === 'result_set') {
      await api.resultSets.update(item.checklist_id, item.id, { folder_id: target.id } as any)
      await refresh()
    } else if (item.type === 'template') {
      await api.checklists.update(item.id, { folder_id: target.id })
      await refresh()
    }
  }, [folder, onUpdateFolder, refresh, t])

  const handleSync = useCallback(({ id }) => {
    confirm(null, t('offlineMode.confirmSync'), () => {
      history.push(`/checklists/offline/sync/${id}`)
    })
  }, [history, t])

  const showMoveDialog = useCallback((item) => {
    const customUI = ({ onClose }) => (
      <MoveFolderDialog
        onClose={onClose}
        onMove={(target) => handleMove(item, target)}
      />
    )
    confirmAlert({ customUI })
  }, [handleMove])

  const handleDeleteChecklist = useCallback(({ id }) => {
    confirm(null, t('resultSet.confirmDelete'), async () => {
      await api.resultSets.deleteResultSet(0, id)
      await refresh()
    })
  }, [refresh, t])

  const handleDeleteFolder = useCallback(({ id }) => {
    confirm(null, t('folder.confirmDelete'), async () => {
      await api.folders.deleteFolder(id)
      const updatedFolder: IFolder = {
        ...folder,
        children: folder.children.filter((c) => c.id !== id)
      }
      onUpdateFolder(updatedFolder.id)
    })
  }, [folder, onUpdateFolder, t])

  const handleSort = useCallback((field: string, direction: 'desc' | 'asc') => {
    if (mode === FolderMode.checklists) {
      dispatch(setActiveResultsSort({ column: field, direction }))
    } else {
      dispatch(setCreatedTempaltesSort({ column: field, direction }))
    }
  }, [dispatch, mode])

  const handlePagination = useCallback((newPage: number) => {
    setPage(newPage)
  }, [])

  const handleBulkCsvExport = useCallback(async (items: any[]) => {
    const folderIds = items.filter((x) => x.type === 'folder').map(((x) => x.id))
    const checklistIds = items.filter((x) => x.type !== 'folder').map((x) => x.id)
    try {
      await api.bulk.generateCsv(folderIds, checklistIds)
      alert(t('bulk.csv_done'))
    } catch (e) {
      onError(e as AxiosError)
    }
  }, [onError, t])


  const handleBulkPdfExport = useCallback(async (items: any[]) => {
    const folderIds = items.filter((x) => x.type === 'folder').map(((x) => x.id))
    const checklistIds = items.filter((x) => x.type !== 'folder').map((x) => x.id)
    try {
      await api.bulk.generateReports(folderIds, checklistIds)
      alert(t('bulk.pdf_done'))
    } catch (e) {
      onError(e as AxiosError)
    }
  }, [onError, t])

  const handleCombinedPdfExport = useCallback(async (items: any[]) => {
    const folderIds = items.filter((x) => x.type === 'folder').map(((x) => x.id))
    const checklistIds = items.filter((x) => x.type !== 'folder').map((x) => x.id)
    try {
      await api.bulk.generateCombinedReport(folderIds, checklistIds)
      alert(t('bulk.pdf_done'))
    } catch (e) {
      onError(e as AxiosError)
    }
  }, [onError, t])

  const handleSummaryReport = useCallback(async (items: any[]) => {
    const folderIds = items.filter((x) => x.type === 'folder').map(((x) => x.id))
    const checklistIds = items.filter((x) => x.type !== 'folder').map((x) => x.id)
    try {
      await api.bulk.generateSummaryReport({ folderIds, checklistIds })
      toast(t('summaryReport.started'), { type: 'success', autoClose: 8000 })
    } catch (e) {
      onError(e as AxiosError)
    }
  }, [onError, t])

  const handleBulkExcelExport = useCallback(async (items: any[]) => {
    const folderIds = items.filter((x) => x.type === 'folder').map(((x) => x.id))
    const checklistIds = items.filter((x) => x.type !== 'folder').map((x) => x.id)
    try {
      await api.bulk.generateExcel(folderIds, checklistIds)
      alert(t('bulk.excel_done'))
    } catch (e) {
      onError(e as AxiosError)
    }
  }, [onError, t])

  const handleBulkDelete = useCallback(async (items: any[]) => {
    const { folderIds, checklistIds, templateIds } = getBulkIds(items)

    confirm(null, t('bulk.confirm_delete'), async () => {
      try {
        await api.bulk.bulkDelete(folderIds, checklistIds, templateIds)
        await refresh()
      } catch (e) {
        onError(e as AxiosError)
      }
    })
  }, [onError, refresh, t])

  const handleBulkMove = useCallback(async (items: any[]) => {
    const { folderIds, checklistIds, templateIds } = getBulkIds(items)

    const customUI = ({ onClose }) => (
      <MoveFolderDialog
        onClose={onClose}
        onMove={async (target) => {
          await api.bulk.move(folderIds, checklistIds, templateIds, target.id)
          await refresh()
        }}
      />
    )
    confirmAlert({ customUI })
  }, [refresh])

  const handleCreateFolder = useCallback(async (updatedFolder: IFolder, newFolder: IFolder) => {
    const f: IFolder = {
      ...updatedFolder,
      children: [...updatedFolder.children, newFolder]
        .sort((a, b) => a.name.localeCompare(b.name))
    }
    await refresh()
    onUpdateFolder(f.id)
    setLastNewFolderId(newFolder.id)
  }, [onUpdateFolder, refresh])

  const editModal = selectedEditResultSet ? (
    (
      <EditModalWrapper
        id={selectedEditResultSet}
        onSubmit={handleResultSetUpdated}
        onClose={handleEditModalClose}
      />
    )) : null

  const handleCreateChecklist = useCallback(({ id }: { id: number }) => {
    history.push(`/resultset/${id}/new`)
  }, [history])

  const multiSelectActions = useMemo(() => [
    {
      label: t('bulk.generate_pdf'),
      callback: handleBulkPdfExport,
      show: checklistStatus === 'closed'
    },
    {
      label: t('bulk.generate_combined_pdf'),
      callback: handleCombinedPdfExport,
      show: checklistStatus === 'closed'
    },
    {
      label: t('summaryReport.bulkAction'),
      callback: handleSummaryReport,
      show: checklistStatus === 'closed' && isAdmin
    },
    {
      label: t('bulk.generate_csv'),
      callback: handleBulkCsvExport,
      show: true // CSV export should be available for all statuses
    },
    {
      label: t('bulk.generate_excel'),
      callback: handleBulkExcelExport,
      show: checklistStatus === 'closed'
    },
    {
      label: t('bulk.delete'),
      callback: handleBulkDelete,
      show: isAdmin && (checklistStatus !== 'closed' || isCorporationAdmin),
      isDanger: true
    },
    { label: t('bulk.move'), callback: handleBulkMove }
  ], [checklistStatus, handleBulkDelete, handleBulkExcelExport, handleBulkMove,
    handleBulkPdfExport, handleBulkCsvExport, handleCombinedPdfExport, handleSummaryReport, isAdmin, isCorporationAdmin, t])

  useEffect(() => { onSummary?.(summary) }, [onSummary, summary])

  // the actions of a row: the same for the old table and the new list ("…" menu)
  const tableActions = [
            {
              icon: 'fas fa-pencil-alt has-pointer',
              tooltip: t('common.edit'),
              onClick: handleEditStart,
              isValidForItem: (item: any) => checkValidEditItem(item) && item.type !== 'folder'
            }, {
              icon: 'fas fa-pencil-alt has-pointer',
              tooltip: t('common.rename'),
              onClick: handleEditStart,
              isValidForItem: (item: any) => checkValidEditItem(item) && item.type === 'folder'
            },
            {
              icon: 'fas fa-unlock has-pointer',
              onClick: handleUnlock,
              tooltip: t('resultSet.unlock'),
              isValidForItem: (item: any) => isAdmin && item.type === 'result_set' && item.locked
            },
            {
              icon: 'fas fa-lock-open has-pointer',
              onClick: handleReopen,
              tooltip: t('resultSet.reopen'),
              isValidForItem: (item: any) => (isAdmin || item.employees_can_restart) && item.type === 'result_set'
                && item.status === 'closed',
              isDisabledForItem: () => isOffline
            },
            {
              icon: 'far fa-play-circle has-pointer',
              onClick: handleCreateChecklist as any,
              tooltip: t('CreateChecklistFromTemplate'),
              isValidForItem: (item: any) => item.type === 'template'
            },
            {
              icon: 'far fa-copy has-pointer',
              onClick: handleClone,
              tooltip: t('CloneChecklistTemplate'),
              isValidForItem: (item: any) => isAdmin && item.type === 'template',
              isDisabledForItem: () => isOffline
            },
            {
              icon: 'far fa-copy has-pointer',
              onClick: handleClone,
              tooltip: t('resultSet.copy'),
              isValidForItem: (item: any) => isAdmin && item.type === 'result_set',
              isDisabledForItem: () => isOffline
            },
            {
              icon: 'fas fa-arrow-right has-pointer',
              onClick: showMoveDialog,
              tooltip: t('folder.moveToFolder'),
              isValidForItem: () => isAdmin,
              isDisabledForItem: () => isOffline
            },
            {
              icon: 'fas fa-download has-pointer',
              onClick: updateOfflineChecklist,
              tooltip: t('offlineMode.updateChecklist'),
              isValidForItem: (item: any) => item.status !== 'closed' && item.type === 'result_set'
                && use_offline_mode && item.isOffline,
              isDisabledForItem: () => isOffline,
              className: 'offline-mode-action'
            },
            {
              icon: 'fas fa-upload',
              onClick: handleSync,
              tooltip: t('offlineMode.syncChecklist'),
              isValidForItem: (item: any) => item.type === 'resultSet' && item.status !== 'closed' && use_offline_mode,
              isDisabledForItem: () => isOffline
            },
            {
              icon: 'fas fa-trash-alt has-pointer',
              tooltip: t('common.delete'),
              onClick: handleDeleteChecklist,
              isValidForItem: (item: any) => item.can_delete && isAdmin
                && item.type === 'result_set' && (item.status !== 'closed' || isCorporationAdmin),
              className: 'is-danger'
            },
            {
              icon: 'fas fa-trash-alt has-pointer',
              tooltip: t('common.delete'),
              onClick: handleDeleteFolder,
              isValidForItem: ((item: any) => item.can_delete && item.type === 'folder'),
              className: 'is-danger'
            },
            {
              icon: 'fas fa-trash-alt has-pointer',
              onClick: handleDeleteTemplate,
              tooltip: t('common.delete'),
              isValidForItem: (item: any) => item.can_delete && item.type === 'template',
              className: 'is-danger',
              isDisabledForItem: () => isOffline
            }
  ]

  const Wrapper = mode === FolderMode.checklists ? TableStyles : Box
  const newDesign = useNewDesign()
  // new design lists: processes (Offen, Erledigt, Eingeladen) and templates (Vorlagen)
  const newList = newDesign && (mode === FolderMode.checklists || mode === FolderMode.templates)

  return (
    <div className={clsx('folder', isMobileOnly && 'folder-mobile', newList && 'is-new-design')}>
      {newList ? null : (
      <div className={`folder-navigation ${isMobileOnly ? 'is-mobile' : 'is-not-mobile'}`}>
        <FolderBreadCrumbs folder={folder} onNavigation={(f) => onSelect({ type: 'folder', item: f })} />
        <Hidden mdDown>
          <SplitButton folder={folder} onCreateFolder={handleCreateFolder} />
        </Hidden>
      </div>
      )}
      {error && (
        <ErrorView error={error} />
      )}
      {/* On mobile only show the spinner for the initial load (no items yet).
          Subsequent page loads are indicated by the sentinel spinner below. */}
      {isLoading && (!isMobileOnly || mobileItems.length === 0) && (
        <LoadSpinner className="centered" />
      )}
      {newDesign && mode === FolderMode.templates ? (
        <TemplateListV2
          rows={tableData}
          raw={(isMobileOnly ? mobileItems : data) as any}
          offlineIds={offlineIds}
          actions={tableActions}
          multiSelectActions={multiSelectActions}
          sortColumn={sortSettings.column}
          sortDir={sortSettings.direction}
          onSort={handleSort}
          page={page}
          totalPages={isMobileOnly ? 1 : totalPages}
          onPagination={handlePagination}
          onOpenFolder={onUpdateFolder}
          isLoading={isLoading}
          // same rule as the old table: the editor for people who may edit, else the start page
          linkOf={(x) => (isMobileOnly || !(x.can_edit || isAdmin) || isOffline ? `/resultset/${x.id}/new` : `/templates/editor/${x.id}`)}
          breadcrumb={folder.parent ? (
            <FolderPath folder={folder} onNavigation={(f) => onSelect({ type: 'folder', item: f })} />
          ) : null}
          toolbar={(
            <Hidden mdDown>
              <Box className="nd-secondary-split"><SplitButton folder={folder} onCreateFolder={handleCreateFolder} /></Box>
            </Hidden>
          )}
        />
      ) : newDesign && mode === FolderMode.checklists ? (
        <ProcessListV2
          rows={tableData}
          raw={isMobileOnly ? mobileItems : data}
          status={checklistStatus}
          tabIndex={tabIndex}
          folder={folder}
          offlineIds={offlineIds}
          summary={summary}
          quick={quick}
          onQuick={(value) => { setQuick(value); setPage(1); setMobileItems([]) }}
          actions={tableActions}
          multiSelectActions={multiSelectActions}
          sortColumn={sortSettings.column}
          sortDir={sortSettings.direction}
          onSort={handleSort}
          page={page}
          totalPages={isMobileOnly ? 1 : totalPages}
          onPagination={handlePagination}
          selectedId={selectedId?.toString()}
          onOpenFolder={onUpdateFolder}
          isLoading={isLoading}
          breadcrumb={folder.parent ? (
            <FolderPath folder={folder} onNavigation={(f) => onSelect({ type: 'folder', item: f })} />
          ) : null}
          toolbar={(
            <>
              <Hidden mdDown>
                <Box className="nd-secondary-split"><SplitButton folder={folder} onCreateFolder={handleCreateFolder} /></Box>
              </Hidden>
              {/* phones: the Vorlagen tab starts processes; the button only crowded the filter row */}
              <Hidden smDown>
              <MuiButton
                variant="contained"
                disableElevation
                onClick={() => history.push('/templates')}
                startIcon={<i className="fas fa-play" style={{ fontSize: 11 }} />}
                sx={{
                  height: 40, px: 2.25, borderRadius: '10px', textTransform: 'none', fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap'
                }}
              >
                {t('processList.startProcess')}
              </MuiButton>
              </Hidden>
            </>
          )}
        />
      ) : (
      <Wrapper>
        <DataTable
          selectable
          data={tableData}
          isLoading={isMobileOnly ? mobileItems.length === 0 && isLoading : isLoading}
          headers={headers}
          sortColumn={sortSettings.column}
          sortDir={sortSettings.direction}
          actions={tableActions}
          onSort={handleSort}
          currentPage={page}
          totalPages={isMobileOnly ? 1 : totalPages}
          selectedId={selectedId?.toString()}
          onPagination={handlePagination}
          multiSelectActions={multiSelectActions}
        />
      </Wrapper>
      )}
      {/* Infinite scroll sentinel — sits just below the list.
          IntersectionObserver watches this div and triggers the next page load. */}
      {isMobileOnly && (
        <>
          <div ref={sentinelRef} style={{ height: 1 }} />
          {isLoading && page > 1 && <LoadSpinner className="centered" />}
        </>
      )}
      { editModal }
    </div>
  )
}
